import { useMsal } from '@azure/msal-react';
import {
  useDeleteMeetingCalendarEventMutation,
  useDeleteMeetingMutation,
  useDeleteRecurringSeriesMutation,
} from '@consigli/hooks';
import { Meeting } from '@consigli/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { MeetingSeries } from '../use-grouped-meetings';
import { isForbiddenError } from '../utils';

interface UseMeetingDeletionParams {
  projectId: number;
}

export const useMeetingDeletion = ({ projectId }: UseMeetingDeletionParams) => {
  const { t } = useTranslation();
  const { accounts, instance } = useMsal();
  const [deleteMeeting] = useDeleteMeetingMutation();
  const [deleteMeetingCalendarEvent] = useDeleteMeetingCalendarEventMutation();
  const [deleteRecurringSeries] = useDeleteRecurringSeriesMutation();
  const [confirmDeleteMeeting, setConfirmDeleteMeeting] = useState<Meeting | null>(null);
  const [confirmDeleteSeries, setConfirmDeleteSeries] = useState<MeetingSeries | null>(null);

  const isRecurringOccurrence = (meeting: Meeting): boolean => !!meeting.recurringSeriesId;

  const handleDeleteMeeting = async (meeting: Meeting) => {
    const { id: meetingId, title } = meeting;
    if (isRecurringOccurrence(meeting)) {
      console.warn('Refusing to delete a single occurrence of a recurring series:', meetingId);
      return;
    }
    try {
      await deleteMeeting({
        meetingId,
        projectId,
      }).unwrap();

      toast.success(t('meeting-secretary.meetings.delete-success', { title }));
    } catch (error) {
      console.error('Error deleting meeting:', error);
      if (isForbiddenError(error)) {
        toast.info(t('meeting-secretary.meetings.delete-forbidden', { title }));
      } else {
        toast.error(t('meeting-secretary.meetings.delete-error', { title }));
      }
    }
  };

  const handleDeleteMeetingWithCalendar = async (meeting: Meeting) => {
    const { id: meetingId, title } = meeting;
    if (isRecurringOccurrence(meeting)) {
      console.warn('Refusing to delete a single occurrence of a recurring series:', meetingId);
      return;
    }
    try {
      const account = accounts[0];
      if (!account) {
        throw new Error('No authenticated account found');
      }

      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['Calendars.ReadWrite', 'OnlineMeetings.ReadWrite', 'User.Read'],
        account,
      });

      await deleteMeetingCalendarEvent({
        meetingId,
        projectId,
        msAccessToken: tokenResponse.accessToken,
        userEmail: account.username || '',
        userId: account.localAccountId || '',
      }).unwrap();

      toast.success(t('meeting-secretary.meetings.delete-success', { title }));
    } catch (error) {
      console.error('Error deleting meeting and calendar event:', error);
      if (isForbiddenError(error)) {
        toast.info(t('meeting-secretary.meetings.delete-forbidden', { title }));
      } else {
        toast.error(t('meeting-secretary.meetings.delete-error', { title }));
      }
    }
  };

  const handleDeleteSeries = async (
    seriesIds: string[],
    title: string,
    deleteFromOutlook = false,
  ) => {
    try {
      const account = accounts[0];
      if (!account) {
        throw new Error('No authenticated account found');
      }
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['Calendars.ReadWrite', 'OnlineMeetings.ReadWrite', 'User.Read'],
        account,
      });
      const calendarAuth = {
        msAccessToken: tokenResponse.accessToken,
        userEmail: account.username || '',
        userId: account.localAccountId || '',
      };

      const results = await Promise.allSettled(
        seriesIds.map((seriesId) =>
          deleteRecurringSeries({
            recurringSeriesId: seriesId,
            projectId,
            deleteFromOutlook,
            ...calendarAuth,
          }).unwrap(),
        ),
      );

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        throw (failures[0] as PromiseRejectedResult).reason;
      }

      toast.success(t('meeting-secretary.meetings.delete-series-success', { title }));
    } catch (error) {
      console.error('Error deleting recurring series:', error);
      if (isForbiddenError(error)) {
        toast.info(t('meeting-secretary.meetings.delete-series-forbidden', { title }));
      } else {
        toast.error(t('meeting-secretary.meetings.delete-series-error', { title }));
      }
    }
  };

  const seriesHasCalendarEvent =
    confirmDeleteSeries?.meetings.some((m) => !!m.calendarEventId) ?? false;

  const currentUserEmail = accounts[0]?.username?.toLowerCase() ?? '';
  const isMeetingCreator = (meeting: Meeting): boolean =>
    !!meeting.organizerEmail && meeting.organizerEmail.toLowerCase() === currentUserEmail;

  const canDeleteMeetingCalendar = confirmDeleteMeeting
    ? !!confirmDeleteMeeting.calendarEventId && isMeetingCreator(confirmDeleteMeeting)
    : false;
  const isDeletingOthersMeeting = confirmDeleteMeeting
    ? !isMeetingCreator(confirmDeleteMeeting)
    : false;

  const isSeriesCreator = confirmDeleteSeries
    ? confirmDeleteSeries.meetings.some((m) => isMeetingCreator(m))
    : false;
  const canDeleteSeriesCalendar = seriesHasCalendarEvent && isSeriesCreator;
  const isDeletingOthersSeries = confirmDeleteSeries ? !isSeriesCreator : false;

  return {
    confirmDeleteMeeting,
    setConfirmDeleteMeeting,
    confirmDeleteSeries,
    setConfirmDeleteSeries,
    handleDeleteMeeting,
    handleDeleteMeetingWithCalendar,
    handleDeleteSeries,
    isMeetingCreator,
    canDeleteMeetingCalendar,
    isDeletingOthersMeeting,
    canDeleteSeriesCalendar,
    isDeletingOthersSeries,
  };
};
